import { useState, type ReactNode } from 'react';
import { Card, Spinner } from '@/ui/primitives.js';
import { Icon } from '@/ui/icons.js';
import { LEGACY_DAY, listCoachDays, type CoachDay } from './coachThread.js';

/**
 * Earlier days with Darwin. Closed by default and fetched only when opened:
 * the list is one small index document per day, never the messages.
 */
export function CoachArchive({ uid, onOpen }: { uid: string; onOpen(day: CoachDay): void }): ReactNode {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState<CoachDay[] | null>(null);
  const [failed, setFailed] = useState(false);

  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    if (next && days === null) {
      setFailed(false);
      void listCoachDays(uid)
        .then(setDays)
        .catch(() => setFailed(true));
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-1 text-sm font-medium text-ink-soft"
      >
        Earlier conversations
        <Icon name={open ? 'minus' : 'plus'} size={16} className="text-muted" />
      </button>
      {open ? (
        failed ? (
          <p className="text-sm text-muted">Could not load earlier conversations. Check your connection.</p>
        ) : days === null ? (
          <Card className="grid place-items-center py-4">
            <Spinner label="Loading" />
          </Card>
        ) : days.length === 0 ? (
          <p className="text-sm text-muted">Nothing yet — each day starts a fresh conversation.</p>
        ) : (
          <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card">
            {days.map((day) => (
              <li key={day.dayKey}>
                <button
                  type="button"
                  onClick={() => onOpen(day)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                >
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-muted">{dayLabel(day.dayKey)}</span>
                    <span className="block truncate text-sm text-ink">{day.firstQuestion || 'Conversation'}</span>
                  </span>
                  <Icon name="back" size={16} className="shrink-0 rotate-180 text-muted" />
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

export function dayLabel(dayKey: string): string {
  if (dayKey === LEGACY_DAY) return 'Before daily conversations';
  const [y, m, d] = dayKey.split('-').map(Number);
  if (!y || !m || !d) return dayKey;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
